import React, { useMemo, useState } from "react";
import moment from "moment";
import AuthInput from "../../../components/Input/AuthInput";
import CustomPagination from "../../../components/TablePagination/TablePagination";
import { FiChevronDown, FiInbox, FiPackage, FiUsers } from "react-icons/fi";
import { formatDateTime } from "../../../utils/dateTime";

const formatRs = (n) =>
  Number(n || 0).toLocaleString("en-PK", { maximumFractionDigits: 0 });

const BuiltiCard = ({ group, expanded, onToggle }) => {
  const stockQty = Math.max(
    0,
    Number(group.total_qty || 0) - Number(group.direct_qty || 0)
  );

  const customerNames = useMemo(() => {
    const names = (group.customers || [])
      .map((c) => c.customer_name)
      .filter(Boolean);
    return [...new Set(names)];
  }, [group.customers]);

  return (
    <div className="rounded-2xl border border-line bg-card shadow-soft overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex flex-wrap items-start justify-between gap-3 p-4 text-left hover:bg-subtle/80 transition-colors"
      >
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <span className="text-xs font-bold px-2.5 py-1 rounded-full border bg-[#155f60]/10 text-[#155f60] border-[#155f60]/20">
              Builti #{group.builti_no}
            </span>
            <span className="text-xs font-semibold text-muted" title="Entry date">
              {group.f_entry_datetime ||
                group.f_date ||
                formatDateTime(group.entry_date || group.date)}
            </span>
            {group.f_stock_date && group.f_stock_date !== group.f_date && (
              <span
                className="text-[10px] text-muted"
                title="Stock / builti date"
              >
                Stock {group.f_stock_date}
              </span>
            )}
          </div>
          <p className="text-sm font-bold text-ink">{group.company}</p>
          {customerNames.length > 0 && (
            <p className="text-xs font-semibold text-[#155f60] mt-0.5">
              Customer: {customerNames.join(", ")}
            </p>
          )}
          <p className="text-xs text-muted mt-0.5">
            Truck: {group.truck_no} · {group.items?.length || 0} item
            {(group.items?.length || 0) !== 1 ? "s" : ""} ·{" "}
            {group.customers?.length || 0} dispatch
            {(group.customers?.length || 0) !== 1 ? "es" : ""}
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-wide text-muted font-semibold">
              Total Qty
            </p>
            <p className="text-sm font-bold text-ink tabular-nums">
              {formatRs(group.total_qty)}
            </p>
          </div>
          <FiChevronDown
            className={`text-muted transition-transform duration-200 ${
              expanded ? "rotate-180" : ""
            }`}
          />
        </div>
      </button>

      {expanded && (
        <div className="border-t border-line px-4 pb-4 pt-3 space-y-4">
          <div className="grid grid-cols-3 gap-2">
            <div className="rounded-lg bg-successSoft border border-success/20 px-3 py-2">
              <p className="text-[10px] font-bold uppercase text-success">
                Received
              </p>
              <p className="text-sm font-extrabold text-success tabular-nums">
                {formatRs(group.total_qty)}
              </p>
            </div>
            <div className="rounded-lg bg-warningSoft border border-warning/20 px-3 py-2">
              <p className="text-[10px] font-bold uppercase text-warning">
                Sent to customers
              </p>
              <p className="text-sm font-extrabold text-warning tabular-nums">
                {formatRs(group.direct_qty || 0)}
              </p>
            </div>
            <div className="rounded-lg bg-subtle border border-line px-3 py-2">
              <p className="text-[10px] font-bold uppercase text-inkSecondary">
                In stock
              </p>
              <p className="text-sm font-extrabold text-ink tabular-nums">
                {formatRs(stockQty)}
              </p>
            </div>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-2">
              <FiPackage className="text-[#155f60]" />
              <h4 className="text-xs font-bold uppercase tracking-wide text-inkSecondary">
                Items received
              </h4>
            </div>
            <div className="overflow-x-auto rounded-lg border border-line">
              <table className="w-full text-left text-sm">
                <thead className="bg-subtle text-xs uppercase text-muted">
                  <tr>
                    <th className="px-3 py-2 font-bold">Item</th>
                    <th className="px-3 py-2 font-bold">Unit</th>
                    <th className="px-3 py-2 font-bold text-right">Qty</th>
                    <th className="px-3 py-2 font-bold text-right">Rate</th>
                    <th className="px-3 py-2 font-bold text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {(group.items || []).map((item) => (
                    <tr key={item._id} className="border-t border-line">
                      <td className="px-3 py-2 font-semibold text-ink">
                        {item.name}
                      </td>
                      <td className="px-3 py-2 text-muted">{item.unit}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-medium">
                        {formatRs(item.qty)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-inkSecondary">
                        {formatRs(item.purchase)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums font-bold">
                        {formatRs(item.total_amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-2">
              <FiUsers className="text-[#155f60]" />
              <h4 className="text-xs font-bold uppercase tracking-wide text-inkSecondary">
                Customers dispatched
              </h4>
            </div>
            {!group.customers?.length ? (
              <p className="text-sm text-muted rounded-lg border border-dashed border-line px-3 py-4 text-center">
                No direct customer dispatch linked to this builti yet.
              </p>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-line">
                <table className="w-full text-left text-sm">
                  <thead className="bg-subtle text-xs uppercase text-muted">
                    <tr>
                      <th className="px-3 py-2 font-bold">Customer</th>
                      <th className="px-3 py-2 font-bold">Sale Inv #</th>
                      <th className="px-3 py-2 font-bold">Item</th>
                      <th className="px-3 py-2 font-bold text-right">Qty</th>
                      <th className="px-3 py-2 font-bold text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.customers.map((row, idx) => (
                      <tr
                        key={`${row.sale_invoice_no}-${row.item_name}-${idx}`}
                        className="border-t border-line"
                      >
                        <td className="px-3 py-2 font-semibold text-ink">
                          {row.customer_name}
                        </td>
                        <td className="px-3 py-2 text-inkSecondary">
                          #{row.sale_invoice_no}
                        </td>
                        <td className="px-3 py-2 text-inkSecondary">
                          {row.item_name}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums font-medium">
                          {formatRs(row.qty)} {row.unit}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums font-bold">
                          {formatRs(row.amount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const ReportBuiltiList = ({
  builtiGroups = [],
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  searchText,
  setSearchText,
  searchBuilti,
  setSearchBuilti,
}) => {
  const [expandedKey, setExpandedKey] = useState(null);

  const filtered = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    const bq = searchBuilti.trim();
    return builtiGroups.filter((group) => {
      const builtiMatch =
        !bq || String(group.builti_no || "").startsWith(bq);
      const textMatch =
        !q ||
        String(group.company || "").toLowerCase().includes(q) ||
        String(group.truck_no || "").toLowerCase().includes(q) ||
        (group.items || []).some((item) =>
          String(item.name || "").toLowerCase().includes(q)
        ) ||
        (group.customers || []).some((row) =>
          String(row.customer_name || "").toLowerCase().includes(q)
        );
      return builtiMatch && textMatch;
    });
  }, [builtiGroups, searchText, searchBuilti]);

  const grandTotals = useMemo(() => {
    return filtered.reduce(
      (acc, group) => {
        acc.received += Number(group.total_amount || 0);
        acc.qty += Number(group.total_qty || 0);
        acc.dispatched += (group.customers || []).reduce(
          (sum, row) => sum + Number(row.amount || 0),
          0
        );
        return acc;
      },
      { received: 0, qty: 0, dispatched: 0 }
    );
  }, [filtered]);

  if (!builtiGroups.length) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-4 rounded-2xl border-2 border-dashed border-line bg-subtle/80">
        <div className="w-16 h-16 rounded-2xl bg-[#155f60]/10 flex items-center justify-center mb-4">
          <FiInbox className="text-[#155f60]" size={28} />
        </div>
        <p className="font-bold text-inkSecondary text-lg">No builti records</p>
        <p className="text-sm text-muted mt-1 text-center max-w-md">
          No stock / builti invoices found for this date range. Use “Show all”
          or widen the dates above.
        </p>
      </div>
    );
  }

  const paged = filtered.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <div className="flex flex-col w-full gap-3">
      <div className="flex flex-col justify-end items-end gap-2 mb-1">
        <AuthInput
          Value={searchText}
          setValue={setSearchText}
          placeholder="Search company, truck, item or customer"
        />
        <AuthInput
          Value={searchBuilti}
          setValue={setSearchBuilti}
          placeholder="Search builti invoice #"
        />
      </div>

      {filtered.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 rounded-lg border border-[#155f60]/20 bg-[#155f60]/5 px-4 py-3">
          <p className="text-sm font-semibold text-[#155f60]">
            Grand Total — {filtered.length} builti
            {filtered.length !== 1 ? "s" : ""}
          </p>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm tabular-nums font-bold">
            <span className="text-[#155f60]">
              Received Rs {formatRs(grandTotals.received)}
            </span>
            <span className="text-warning border-l border-warning/30 pl-4">
              Dispatched Rs {formatRs(grandTotals.dispatched)}
            </span>
            <span className="text-inkSecondary border-l border-line pl-4">
              Qty {formatRs(grandTotals.qty)}
            </span>
          </div>
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="w-full text-center text-muted py-8 text-sm">
          No matches for your search.
        </div>
      ) : (
        paged.map((group) => {
          const key =
            group.group_key ||
            `${group.company_id || group.company}|${group.builti_no}|${group.entry_date || group.date}|${group.truck_no}`;
          return (
            <BuiltiCard
              key={key}
              group={group}
              expanded={expandedKey === key}
              onToggle={() =>
                setExpandedKey((prev) => (prev === key ? null : key))
              }
            />
          );
        })
      )}

      <CustomPagination
        count={filtered.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={onPageChange}
        RowsPerPage={rowsPerPage}
        onRowsPerPageChange={onRowsPerPageChange}
      />
    </div>
  );
};

export default ReportBuiltiList;
